/**
 * Secure OpenAI API client
 * Uses serverless function to keep API keys secure
 */

/**
 * Makes a secure call to OpenAI API through serverless function
 * @param {Object} params - OpenAI API parameters
 * @param {string} params.model - Model to use (e.g., "gpt-4o-mini")
 * @param {Array} params.messages - Array of message objects with role and content
 * @param {number} [params.temperature] - Temperature setting (default: 0.7)
 * @param {number} [params.max_tokens] - Maximum tokens to generate
 * @returns {Promise<Object>} OpenAI completion response
 */
export async function callOpenAI({ model, messages, temperature, max_tokens }) {
  try {
    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model,
        messages,
        temperature,
        max_tokens
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('OpenAI API call failed:', error);
    throw error;
  }
}

// Legacy export for backward compatibility (will be removed after migration)
// This is kept temporarily to avoid breaking existing code
export const openai = {
  chat: {
    completions: {
      create: async (params) => {
        console.warn('Using legacy OpenAI client. Consider migrating to callOpenAI()');
        return callOpenAI(params);
      }
    }
  }
};
